import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/ImageUpload";
import { api, errorToastOpts, formatApiError } from "@/lib/api";

const EMPTY = { name: "", kind: "original", source_material: "", genre: "", description: "", cover_image: null };

export const WorldFormDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, kind: f.kind, source_material: f.source_material, genre: f.genre, description: f.description, cover_image: f.cover_image };
      const { data } = initial?.id ? await api.put(`/worlds/${initial.id}`, body) : await api.post("/worlds", body);
      toast.success(initial?.id ? "World updated" : "A new world is born");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      if (err?.response?.status === 402) onOpenChange(false);
      toast.error(formatApiError(err), errorToastOpts(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-panel border-line max-w-lg max-h-[90vh] overflow-y-auto" data-testid="world-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-amber-100">{initial?.id ? "Edit world" : "Forge a new world"}</DialogTitle>
          <DialogDescription>An original creation or a corner of someone else's universe.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Name</Label>
            <Input required value={f.name} onChange={set("name")} placeholder="The Ember Archipelago" data-testid="world-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={f.kind} onValueChange={set("kind")}>
                <SelectTrigger data-testid="world-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-panel">
                  <SelectItem value="original" data-testid="world-kind-original">Original work</SelectItem>
                  <SelectItem value="fanfiction" data-testid="world-kind-fanfiction">Fanfiction</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Genre</Label>
              <Input value={f.genre} onChange={set("genre")} placeholder="Dark fantasy" data-testid="world-genre-input" /></div>
          </div>
          {f.kind === "fanfiction" && (
            <div className="space-y-1.5"><Label>Source material / fandom</Label>
              <Input value={f.source_material} onChange={set("source_material")} placeholder="e.g. The Witcher" data-testid="world-source-input" /></div>
          )}
          <div className="space-y-1.5"><Label>Description</Label>
            <Textarea rows={3} value={f.description} onChange={set("description")} placeholder="A one-paragraph pitch of your world" data-testid="world-description-input" /></div>
          <div className="space-y-1.5"><Label>Cover image</Label>
            <ImageUpload value={f.cover_image} onChange={set("cover_image")} worldId={initial?.id} testId="world-cover" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="world-form-submit">
            {busy ? "Saving..." : initial?.id ? "Save changes" : "Create world"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
